import { useTranslation } from 'react-i18next';
import { Document } from '@contentful/rich-text-types';
import { orderBy } from 'lodash';
import { DocumentTitle } from 'components/DocumentTitle';
import { Loader } from 'components/Loader';
import { Button, IconStartAdornment } from 'components/Button';
import { useNavigation } from 'utils/hooks/useNavigation';

import { SupportHelpCard } from 'components/SupportHelpCard';
import { DashboardLayout } from 'components/Layout';
import { useTrack } from 'utils/hooks/useMixPanel';
import { ContentContainer } from 'components/ContentContainer';
import { TypeSupportArticle, TypeSupportSubCategory } from 'types/contentful/clinical';
import { SUPPORT_MIXPANEL_EVENTS } from '../mixpanel';
import { useSupportCategoryPage } from '../hooks/useSupportCategoryPage';
import { SupportCategoryHero } from './components/CategoryHero';
import { Accordion, AccordionItem, RichText } from '@expressable/syntax-ds';

export const SupportCategory = () => {
  const { t } = useTranslation();
  const { navigate, routes } = useNavigation();
  const {
    isLoading,
    selectedCategory,
    categoryArticles,
    article,
    categoryName,
    handlers: { onClickTopic, handleSubCategoryClick },
  } = useSupportCategoryPage();

  useTrack(SUPPORT_MIXPANEL_EVENTS.viewed.category, {
    category: categoryName,
  });

  const { articlesWithSubCategory = [], articlesWithoutSubCategory = [] } = categoryArticles;

  return (
    <>
      <DocumentTitle>{`Support - ${selectedCategory?.fields.categoryTitle}`}</DocumentTitle>

      <ContentContainer className="bg-cream-50 flex flex-col grow" variant="full">
        {isLoading ? (
          <Loader
            data-testid="billing-loader"
            className="flex grow items-center justify-center p-24 text-primary-500"
          />
        ) : (
          <DashboardLayout
            headerItems={
              <>
                <Button
                  variant="link"
                  startAdornment={<IconStartAdornment name="AngleLeft" />}
                  onClick={() => navigate(routes.support.base)}
                  className="flex justify-start"
                  size="sm"
                >
                  {t('support.category.backCTA')}
                </Button>
                <SupportCategoryHero name={categoryName} />
                <div className="grid sm:grid-cols-3 gap-16">
                  {selectedCategory?.fields.supportSubCategories?.map(category => (
                    <Button
                      key={category.sys.id}
                      variant="secondary"
                      size="lg"
                      className="col-span-1 md:mb-64"
                      onClick={() => handleSubCategoryClick(category.fields.supportSubCategoryTitle)}
                    >
                      {category.fields.supportSubCategoryTitle}
                    </Button>
                  ))}
                </div>
              </>
            }
            primaryItems={
              <section className="mt-8">
                {selectedCategory?.fields.supportSubCategories?.map(category => {
                  return (
                    <Accordion
                      id={category.fields.supportSubCategoryTitle}
                      key={category.sys.id}
                      title={category.fields.supportSubCategoryTitle}
                      className="mb-24"
                    >
                      {orderBy(
                        articlesWithSubCategory.filter((supportArticles: TypeSupportArticle) => {
                          if (supportArticles.fields.supportSubCategories?.length) {
                            return supportArticles.fields.supportSubCategories.some(
                              (a: TypeSupportSubCategory) =>
                                a.fields.supportSubCategoryTitle.toLowerCase() ===
                                category.fields.supportSubCategoryTitle.toLowerCase(),
                            );
                          }
                          return false;
                        }),
                        'fields.title',
                        'asc',
                      ).map(supportArticle => {
                        const articleId = article?.split('-')[0];
                        return (
                          <div
                            key={supportArticle.sys.id}
                            id={supportArticle.fields.title}
                            role="article"
                            onClick={() => {
                              onClickTopic(
                                categoryName,
                                `${supportArticle.sys.id}-${supportArticle.fields.title}`,
                                category.fields.supportSubCategoryTitle,
                              );
                            }}
                          >
                            <AccordionItem
                              defaultOpen={articleId === supportArticle.sys.id}
                              title={supportArticle.fields.title}
                            >
                              <RichText>{supportArticle.fields.supportContent as Document}</RichText>
                            </AccordionItem>
                          </div>
                        );
                      })}
                    </Accordion>
                  );
                })}

                {articlesWithoutSubCategory.length > 0 && (
                  <Accordion title="Other" className="mb-24">
                    {articlesWithoutSubCategory.map((supportArticle: TypeSupportArticle) => {
                      const articleId = article?.split('-')[0];
                      return (
                        <div
                          key={supportArticle.sys.id}
                          id={supportArticle.fields.title}
                          role="article"
                          onClick={() =>
                            onClickTopic(
                              categoryName,
                              `${supportArticle.sys.id}-${supportArticle.fields.title}`,
                              'other',
                            )
                          }
                        >
                          <AccordionItem
                            defaultOpen={articleId === supportArticle.sys.id}
                            title={supportArticle.fields.title}
                          >
                            <RichText>{supportArticle.fields.supportContent as Document}</RichText>
                          </AccordionItem>
                        </div>
                      );
                    })}
                  </Accordion>
                )}
              </section>
            }
            secondaryItems={<SupportHelpCard />}
          />
        )}
      </ContentContainer>
    </>
  );
};
